import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { getAllReseller, getResellerComm } from "../../../config/LiveApi";
import { FaCalendar } from "react-icons/fa";
import { DateRangePicker } from "react-date-range";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import Spinner from "../../Spin/Spinner";
import { toast } from "react-toastify";

const AllResellerDashboard = () => {
  const [resellers, setResellers] = useState([]);
  const [commissionData, setCommissionData] = useState({});
  const [loadingId, setLoadingId] = useState(null);
  const [expandedReseller, setExpandedReseller] = useState(null);

  const token = useSelector((store) => store.userReducer.token);

  const [showPicker, setShowPicker] = useState(false);

  const [state, setState] = useState({
    selection: {
      startDate: new Date(),
      endDate: new Date(),
      key: "selection",
    },
  });

  /* ---------------- Date Helpers ---------------- */

  const getStartOfDayISO = (date) => {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    return d.toISOString();
  };

  const getEndOfDayISO = (date) => {
    const d = new Date(date);
    d.setHours(23, 59, 59, 999);
    return d.toISOString();
  };

  /* ---------------- Fetch Resellers ---------------- */

  useEffect(() => {
    const fetchResellers = async () => {
      try {
        const response = await getAllReseller(token);

        if (response.status) {
          setResellers(response.data || []);
        }
      } catch (err) {
        toast.error("Failed to fetch resellers");
      }
    };

    if (token) fetchResellers();
  }, [token]);

  /* ---------------- Fetch Commission ---------------- */

  const fetchCommission = async (resellerId) => {
    try {
      setLoadingId(resellerId);

      const start = getStartOfDayISO(state.selection.startDate);
      const end = getEndOfDayISO(state.selection.endDate);

      const response = await getResellerComm(resellerId, start, end, token);

      if (response.status) {
        setCommissionData((prev) => ({
          ...prev,
          [resellerId]: response.data || [],
        }));
      } else {
        toast.error(response.message || "No Data Found");
      }
    } catch (err) {
      toast.error("Failed to fetch commission");
    } finally {
      setLoadingId(null);
    }
  };

  /* ---------------- Toggle Expand ---------------- */

  const toggleReseller = async (resellerId) => {
    if (expandedReseller === resellerId) {
      setExpandedReseller(null);
      return;
    }

    setExpandedReseller(resellerId);
    await fetchCommission(resellerId);
  };

  /* ---------------- Date Range Validation ---------------- */

  const isRangeWithinOneMonth = (start, end) => {
    const diffTime = new Date(end) - new Date(start);
    const diffDays = diffTime / (1000 * 60 * 60 * 24);
    return diffDays <= 31;
  };

  /* ====================== UI ====================== */

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div style={styles.title}>Dashboard</div>

        <div style={{ position: "relative" }}>
          <button
            style={styles.calendarButton}
            onClick={() => setShowPicker(!showPicker)}
          >
            <FaCalendar />
          </button>

          {showPicker && (
            <div style={styles.pickerWrapper}>
              <DateRangePicker
                ranges={[state.selection]}
                onChange={(item) => {
                  const { startDate, endDate } = item.selection;

                  if (!isRangeWithinOneMonth(startDate, endDate)) {
                    toast.error("You can select maximum 1 month date range");
                    return;
                  }

                  setState({ selection: item.selection });
                }}
                maxDate={new Date()}
              />

              <button
                style={styles.applyButton}
                onClick={async () => {
                  setShowPicker(false);
                  setCommissionData({});
                  if (expandedReseller) {
                    await fetchCommission(expandedReseller);
                  }
                }}
              >
                Apply
              </button>
            </div>
          )}
        </div>
      </div>

      <div style={styles.dateText}>
        Selected Date Range:{" "}
        <strong>
          {new Date(state.selection.startDate).toLocaleDateString("en-GB")} -{" "}
          {new Date(state.selection.endDate).toLocaleDateString("en-GB")}
        </strong>
      </div>

      {resellers.map((reseller) => {
        const isExpanded = expandedReseller === reseller.id;
        const merchants = commissionData[reseller.id] || [];

        /* ----- Totals Calculation ----- */

        const totalPayin = merchants.reduce(
          (sum, m) => sum + Number(m.totalPayin || 0),
          0,
        );

        const totalPayout = merchants.reduce(
          (sum, m) => sum + Number(m.totalPayout || 0),
          0,
        );

        const totalPayinCommission = merchants.reduce(
          (sum, m) => sum + Number(m.payinCommission || 0),
          0,
        );

        const totalPayoutCommission = merchants.reduce(
          (sum, m) => sum + Number(m.payoutCommission || 0),
          0,
        );

        const netCommission = totalPayinCommission + totalPayoutCommission;

        return (
          <div key={reseller.id} style={styles.resellerCard}>
            <div style={styles.resellerHeader}>
              <div style={styles.resellerName}>{reseller.name}</div>

              <button
                style={styles.viewButton}
                onClick={() => toggleReseller(reseller.id)}
              >
                {isExpanded ? "Hide" : "View"}
              </button>
            </div>

            {isExpanded && (
              <>
                {loadingId === reseller.id && <Spinner />}

                {merchants.length > 0 && (
                  <div style={styles.summaryBox}>
                    <div style={styles.summaryItem}>
                      Total Payin: ₹{totalPayin.toFixed(2)}
                    </div>

                    <div style={styles.summaryItem}>
                      Total Payout: ₹{totalPayout.toFixed(2)}
                    </div>

                    <div style={styles.summaryItem}>
                      Payin Commission: ₹{totalPayinCommission.toFixed(2)}
                    </div>

                    <div style={styles.summaryItem}>
                      Payout Commission: ₹{totalPayoutCommission.toFixed(2)}
                    </div>

                    <div style={styles.summaryItem}>
                      Net Commission: ₹{netCommission.toFixed(2)}
                    </div>
                  </div>
                )}

                <div style={styles.tableWrapper}>
                  <table style={styles.table}>
                    <thead>
                      <tr>
                        <th style={styles.th}>Merchant</th>
                        <th style={styles.th}>Payin</th>
                        <th style={styles.th}>Payout</th>
                        <th style={styles.th}>Payin Commission</th>
                        <th style={styles.th}>Payout Commission</th>
                      </tr>
                    </thead>

                    <tbody>
                      {merchants.length > 0 ? (
                        merchants.map((m) => (
                          <tr key={m.merchantId}>
                            <td style={styles.td}>{m.merchantName}</td>
                            <td style={styles.td}>₹{m.totalPayin}</td>
                            <td style={styles.td}>₹{m.totalPayout}</td>
                            <td style={styles.td}>₹{m.payinCommission}</td>
                            <td style={styles.td}>₹{m.payoutCommission}</td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td
                            colSpan="5"
                            style={{ ...styles.td, textAlign: "center" }}
                          >
                            No Data Found
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        );
      })}
    </div>
  );
};

const styles = {
  container: {
    padding: "30px",
    backgroundColor: "#f4f6f9",
    minHeight: "100vh",
    fontFamily: "Segoe UI, sans-serif",
  },

  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "25px",
  },

  title: {
    fontSize: "24px",
    fontWeight: "600",
    color: "#1f2937",
  },

  calendarButton: {
    padding: "10px 14px",
    backgroundColor: "#fc8019",
    color: "#fff",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer",
    boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
  },

  pickerWrapper: {
    position: "absolute",
    right: 0,
    top: "60px",
    backgroundColor: "#fff",
    padding: "15px",
    borderRadius: "12px",
    boxShadow: "0 10px 25px rgba(0,0,0,0.15)",
    zIndex: 1000,
  },

  applyButton: {
    marginTop: "10px",
    padding: "8px 16px",
    backgroundColor: "#fc8019",
    color: "#fff",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
  },

  dateText: {
    fontSize: "14px",
    color: "#4b5563",
    marginBottom: "20px",
  },

  resellerCard: {
    backgroundColor: "#fff",
    borderRadius: "12px",
    padding: "20px",
    marginBottom: "20px",
    boxShadow: "0 4px 12px rgba(0,0,0,0.06)",
  },

  resellerHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },

  resellerName: {
    fontSize: "18px",
    fontWeight: "600",
    color: "#111827",
  },

  viewButton: {
    backgroundColor: "#fc8019",
    border: "none",
    padding: "6px 14px",
    borderRadius: "6px",
    color: "#fff",
    cursor: "pointer",
  },

  summaryBox: {
    display: "flex",
    gap: "20px",
    flexWrap: "wrap",
    backgroundColor: "#f9fafb",
    padding: "15px",
    borderRadius: "10px",
    marginTop: "15px",
    marginBottom: "15px",
  },

  summaryItem: {
    minWidth: "180px",
    fontSize: "14px",
    color: "#374151",
  },

  tableWrapper: {
    backgroundColor: "#fff",
    borderRadius: "10px",
    overflow: "hidden",
    boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
  },

  table: {
    width: "100%",
    borderCollapse: "collapse",
  },

  th: {
    backgroundColor: "#f3f4f6",
    padding: "12px",
    textAlign: "left",
    fontSize: "14px",
    borderBottom: "1px solid #e5e7eb",
  },

  td: {
    padding: "12px",
    fontSize: "14px",
    borderBottom: "1px solid #f1f1f1",
  },
};

export default AllResellerDashboard;
